import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { collection, onSnapshot } from "firebase/firestore";

import "../../css/Pages/SignIn/SignIn.css";
import Logo from "../../assets/icons/logo.svg";
import GoogleIcon from "../../assets/images/google.png";
import MicrosoftIcon from "../../assets/images/microsoft.png";

import {
  auth,
  createGoogleProvider,
  signInWithPopup,
  signOut,
  db as firestore,
} from "../../backend/firebaseConfig";

import SignInButton from "../DesignSystem/Buttons/SignInButtons";

import { useUserRole } from "../../context/UserRoleContext";
import { useUserEmail } from "../../context/UserEmailContext";
import { useAdminMails } from "../../context/AdminEmailsContext";

import {
  CHECK2_USERS_COLLECTION,
  AUTH_NOT_READY,
  CHECK2_ROLES,
  normalizeEmail,
  getAuthenticatedEmail,
  createMicrosoftProvider,
  buildCheck2UsersMap,
} from "../../utils/amlAuthUtils";

const LOG_PREFIX = "[Check2 SignIn]";

const getFriendlyAuthErrorMessage = (providerName, error) => {
  const code = error?.code || "";

  if (code === "auth/popup-closed-by-user") {
    return `${providerName} sign-in was cancelled.`;
  }

  if (code === "auth/popup-blocked") {
    return `${providerName} sign-in popup was blocked. Please allow popups and try again.`;
  }

  if (code === "auth/unauthorized-domain") {
    return "This domain is not authorised in Firebase Authentication settings.";
  }

  if (code === "auth/cancelled-popup-request") {
    return `${providerName} sign-in was interrupted. Please try again.`;
  }

  if (code === "auth/account-exists-with-different-credential") {
    return "An account already exists with a different sign-in method.";
  }

  return `${providerName} sign-in failed. ${code ? `(${code})` : "Please try again."}`;
};

const SignIn = () => {
  const navigate = useNavigate();

  const { setUserRole } = useUserRole();
  const { setUserEmail } = useUserEmail();
  const { setAdminMails } = useAdminMails();

  const [authReady, setAuthReady] = useState(false);
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const check2AuthIndexRef = useRef({
    users: new Map(),
    ready: false,
  });

  useEffect(() => {
    // console.log(`${LOG_PREFIX} Component mounted.`);

    return () => {
      // console.log(`${LOG_PREFIX} Component unmounted.`);
    };
  }, []);

  useEffect(() => {
    if (!errorMessage) return undefined;

    // console.warn(`${LOG_PREFIX} Showing error message:`, errorMessage);

    const timer = setTimeout(() => {
      // console.log(`${LOG_PREFIX} Clearing error message.`);
      setErrorMessage("");
    }, 10000);

    return () => clearTimeout(timer);
  }, [errorMessage]);

  useEffect(() => {
    // console.log(
    //   `${LOG_PREFIX} Loading authorised Check² users from collection:`,
    //   CHECK2_USERS_COLLECTION,
    // );

    const usersRef = collection(firestore, CHECK2_USERS_COLLECTION);

    const unsubscribe = onSnapshot(
      usersRef,
      (snapshot) => {
        // console.log(`${LOG_PREFIX} Authorised users snapshot received.`, {
        //   size: snapshot.size,
        //   empty: snapshot.empty,
        // });

        const usersMap = buildCheck2UsersMap(snapshot);

        check2AuthIndexRef.current = {
          users: usersMap,
          ready: true,
        };

        setAuthReady(true);

        // console.log(`${LOG_PREFIX} Authorised users index ready.`, {
        //   authorisedUserCount: usersMap.size,
        // });
      },
      (error) => {
        // console.error(`${LOG_PREFIX} Error loading Check² users:`, {
        //   code: error?.code,
        //   message: error?.message,
        //   error,
        // });

        check2AuthIndexRef.current = {
          users: new Map(),
          ready: false,
        };

        setAuthReady(false);
        setErrorMessage("Unable to load access permissions. Please try again.");
      },
    );

    return () => {
      // console.log(
      //   `${LOG_PREFIX} Unsubscribing from authorised users snapshot.`,
      // );
      unsubscribe();
    };
  }, []);

  const resolveCheck2Access = (email) => {
    const authIndex = check2AuthIndexRef.current;

    // console.log(`${LOG_PREFIX} Resolving Check² access...`, {
    //   email,
    //   authIndexReady: authIndex.ready,
    //   indexedUsers: authIndex.users.size,
    // });

    if (!authIndex.ready) {
      // console.warn(`${LOG_PREFIX} Auth index is not ready yet.`);
      return AUTH_NOT_READY;
    }

    const normalizedEmail = normalizeEmail(email);
    const check2User = authIndex.users.get(normalizedEmail);

    if (!check2User) {
      // console.warn(`${LOG_PREFIX} User is not authorised for Check².`, {
      //   normalizedEmail,
      // });

      return null;
    }

    // console.log(`${LOG_PREFIX} Authorised user found.`, {
    //   normalizedEmail,
    //   role: check2User.role,
    //   linkedUsersCount: Array.isArray(check2User.users)
    //     ? check2User.users.length
    //     : 0,
    // });

    if (check2User.role === CHECK2_ROLES.SUPERADMIN) {
      // console.log(`${LOG_PREFIX} Applying SUPERADMIN access.`);
      setAdminMails([]);
      return CHECK2_ROLES.SUPERADMIN;
    }

    if (check2User.role === CHECK2_ROLES.ADMIN) {
      // console.log(`${LOG_PREFIX} Applying ADMIN access.`, {
      //   adminUsers: check2User.users || [],
      // });

      setAdminMails(check2User.users || []);
      return CHECK2_ROLES.ADMIN;
    }

    if (check2User.role === CHECK2_ROLES.USER) {
      // console.log(`${LOG_PREFIX} Applying USER access.`);
      setAdminMails([normalizedEmail]);
      return CHECK2_ROLES.USER;
    }

    // console.warn(`${LOG_PREFIX} Unknown Check² role found.`, {
    //   normalizedEmail,
    //   role: check2User.role,
    // });

    return null;
  };

  const completeSignIn = async (provider, providerName) => {
    if (isSigningIn) {
      // console.warn(
      //   `${LOG_PREFIX} Sign-in already in progress. Ignoring click.`,
      // );
      return;
    }

    // console.log(`${LOG_PREFIX} Starting ${providerName} sign-in...`, {
    //   providerName,
    //   authReady,
    // });

    setErrorMessage("");
    setIsSigningIn(true);

    try {
      // console.log(`${LOG_PREFIX} Opening ${providerName} popup...`);

      const result = await signInWithPopup(auth, provider);
      // console.log(`${LOG_PREFIX} ${providerName} popup completed.`, {
      //   hasUser: !!result?.user,
      //   providerId: result?.providerId,
      //   operationType: result?.operationType,
      // });

      // console.log(`${LOG_PREFIX} Firebase user email sources.`, {
      //   directEmail: result?.user?.email || null,
      //   providerData: result?.user?.providerData?.map((provider) => ({
      //     providerId: provider?.providerId,
      //     email: provider?.email || null,
      //   })),
      //   reloadUserInfoEmail: result?.user?.reloadUserInfo?.email || null,
      // });

      const email = getAuthenticatedEmail(result.user);

      // console.log(`${LOG_PREFIX} Authenticated email extracted.`, {
      //   email,
      // });

      if (!email) {
        // console.warn(`${LOG_PREFIX} No email returned from ${providerName}.`);

        await signOut(auth);

        setErrorMessage(`Unable to retrieve your email from ${providerName}.`);
        return;
      }

      const normalizedEmail = normalizeEmail(email);

      // console.log(`${LOG_PREFIX} Normalised authenticated email.`, {
      //   normalizedEmail,
      // });

      const role = resolveCheck2Access(normalizedEmail);

      // console.log(`${LOG_PREFIX} Role resolution result.`, {
      //   normalizedEmail,
      //   role,
      // });

      if (role === AUTH_NOT_READY) {
        // console.warn(`${LOG_PREFIX} Access index not ready after sign-in.`);

        setErrorMessage("Check² access is still loading. Please try again.");
        return;
      }

      if (!role) {
        // console.warn(
        //   `${LOG_PREFIX} Signed-in user is not authorised. Signing out.`,
        //   {
        //     normalizedEmail,
        //   },
        // );

        await signOut(auth);

        setErrorMessage("Your email is not authorised to access Check².");
        return;
      }

      // console.log(`${LOG_PREFIX} Clearing previous localStorage state...`);
      localStorage.clear();

      // console.log(`${LOG_PREFIX} Saving authenticated session locally.`, {
      //   normalizedEmail,
      //   role,
      // });

      setUserRole(role);
      setUserEmail(normalizedEmail);

      localStorage.setItem("check2UserRole", role);
      localStorage.setItem("check2UserEmail", normalizedEmail);

      // console.log(`${LOG_PREFIX} Navigating to /my-checks...`);
      navigate("/my-checks", { replace: true });
    } catch (error) {
      // console.error(`${LOG_PREFIX} ${providerName} sign-in failed.`, {
      //   code: error?.code,
      //   message: error?.message,
      //   customData: error?.customData,
      //   error,
      // });

      setErrorMessage(getFriendlyAuthErrorMessage(providerName, error));
    } finally {
      // console.log(`${LOG_PREFIX} ${providerName} sign-in flow finished.`);
      setIsSigningIn(false);
    }
  };

  const handleGoogleSignIn = () => {
    // console.log(`${LOG_PREFIX} Google sign-in button clicked.`);

    const provider = createGoogleProvider();

    completeSignIn(provider, "Google");
  };

  const handleMicrosoftSignIn = () => {
    // console.log(`${LOG_PREFIX} Microsoft sign-in button clicked.`);

    const provider = createMicrosoftProvider();

    completeSignIn(provider, "Microsoft");
  };

  const buttonsDisabled = !authReady || isSigningIn;

  // console.log(`${LOG_PREFIX} Render state.`, {
  //   authReady,
  //   isSigningIn,
  //   buttonsDisabled,
  //   hasError: !!errorMessage,
  //   indexedUsers: check2AuthIndexRef.current.users.size,
  // });

  return (
    <div className="SignIn-Page">
      <Helmet>
        <title>check² | Sign In</title>
        <meta name="description" content="Sign in to Check²" />
      </Helmet>

      <div className="SignIn-Bg">
        <div className="SignIn-BgGrid" />
        <div className="SignIn-Orb SignIn-Orb--a" />
        <div className="SignIn-Orb SignIn-Orb--b" />
        <div className="SignIn-Orb SignIn-Orb--c" />
      </div>

      <main className="SignIn-Container">
        <section className="SignIn-Hero">
          <div className="SignIn-Brand">
            <img src={Logo} alt="Check² Logo" className="SignIn-Logo" />
          </div>

          <div className="SignIn-Copy">
            <div className="SignIn-Pills">
              <span>AML checks</span>
              <span>ID verification</span>
            </div>

            <h1>
              <span>
                Where Smart Onboarding Meets Compliance. Confidence at Every
                Checkpoint.
              </span>
            </h1>

            <p>
              Sign in to create checks, review verification outcomes, and manage
              compliance workflows in one secure place.
            </p>

            <div className="SignIn-Stats">
              <div>
                <strong>One view</strong>
                <span>Full check journey</span>
              </div>

              <div>
                <strong>Secure</strong>
                <span>Google or Microsoft SSO</span>
              </div>
            </div>
          </div>
        </section>

        <section className="SignIn-Card">
          <div className="SignIn-CardHeader">
            <h2>Sign in</h2>
            <p>Use your authorised account to continue.</p>
          </div>

          {errorMessage && (
            <div className="SignIn-Error" role="alert">
              {errorMessage}
            </div>
          )}

          {!authReady && (
            <div className="SignIn-Loading">Loading access permissions…</div>
          )}

          <div className="SignIn-Form">
            <SignInButton
              imgSrc={GoogleIcon}
              title={isSigningIn ? "Signing in..." : "Sign in with Google"}
              onClick={handleGoogleSignIn}
              disabled={buttonsDisabled}
            />

            <SignInButton
              imgSrc={MicrosoftIcon}
              title={isSigningIn ? "Signing in..." : "Sign in with Microsoft"}
              onClick={handleMicrosoftSignIn}
              disabled={buttonsDisabled}
            />
          </div>

          <div className="SignIn-FooterText">
            Access is restricted to authorised check² users only.
          </div>
        </section>
      </main>
    </div>
  );
};

export default SignIn;